/**
 * SecurityOfficersManagement - Manage security officer accounts (Super Admin only)
 * Now includes role selection from security_roles collection
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Shield, UserPlus, Edit2, Trash2, Check, X, Eye, EyeOff,
  Phone, Mail, BadgeCheck, MapPin, Clock, MoreHorizontal, Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SecurityOfficersManagement = () => {
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingOfficer, setEditingOfficer] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch officers
  const { data: officers = [], isLoading } = useQuery({
    queryKey: ['security-officers'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/security-officers`, { headers });
      if (!res.ok) throw new Error('Failed to fetch officers');
      return res.json();
    }
  });

  // Fetch security roles
  const { data: roles = [] } = useQuery({
    queryKey: ['security-roles'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/security-roles`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Create officer
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/security-officers`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to create officer');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['security-officers']);
      setShowAddDialog(false);
      toast.success('Security officer created successfully');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update officer
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/api/admin/security-officers/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to update officer');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['security-officers']);
      setEditingOfficer(null);
      toast.success('Security officer updated');
    },
    onError: (error) => toast.error(error.message)
  });

  // Delete officer
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/admin/security-officers/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error('Failed to delete officer');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['security-officers']);
      toast.success('Security officer deleted');
    },
    onError: (error) => toast.error(error.message)
  });

  // Toggle active status
  const toggleActive = (officer) => {
    updateMutation.mutate({ 
      id: officer.id, 
      data: { is_active: !officer.is_active } 
    });
  };

  const gateOptions = ['main_gate', 'north_gate', 'south_gate', 'parking_entrance', 'parking_exit', 'reception', 'loading_dock'];

  const OfficerForm = ({ officer, onSubmit, onCancel, isLoading }) => {
    const [formData, setFormData] = useState({
      email: officer?.email || '',
      password: '',
      name: officer?.name || '',
      badge_number: officer?.badge_number || '',
      phone: officer?.phone || '',
      role_id: officer?.role_id || '',
      assigned_gates: officer?.assigned_gates || []
    });

    const toggleGate = (gate) => {
      setFormData(prev => ({
        ...prev,
        assigned_gates: prev.assigned_gates.includes(gate)
          ? prev.assigned_gates.filter(g => g !== gate)
          : [...prev.assigned_gates, gate]
      }));
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      const data = { ...formData };
      if (!data.password && officer) {
        delete data.password; // Don't send empty password on update
      }
      if (!data.role_id) {
        delete data.role_id; // Don't send empty role_id
      }
      onSubmit(data);
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Full Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="John Smith"
              data-testid="input-officer-name"
            />
          </div>

          <div>
            <Label>Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              placeholder="security@company.com"
              data-testid="input-officer-email"
            />
          </div>

          <div>
            <Label>Password {officer ? '(leave blank to keep)' : '*'}</Label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!officer}
                placeholder={officer ? '••••••••' : 'Enter password'}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label>Badge Number</Label>
            <Input
              value={formData.badge_number}
              onChange={(e) => setFormData({ ...formData, badge_number: e.target.value })}
              placeholder="SEC-001"
            />
          </div>

          <div>
            <Label>Phone</Label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+1234567890"
            />
          </div>

          <div className="col-span-2">
            <Label>Role</Label>
            <Select 
              value={formData.role_id} 
              onValueChange={(value) => setFormData({ ...formData, role_id: value })}
            >
              <SelectTrigger data-testid="select-officer-role">
                <SelectValue placeholder="Select a role..." />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: role.color || '#3B82F6' }}
                      />
                      {role.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">
              Role determines permissions for this officer
            </p>
          </div>

          <div className="col-span-2">
            <Label>Assigned Gates</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {gateOptions.map(gate => (
                <Badge
                  key={gate}
                  variant={formData.assigned_gates.includes(gate) ? 'default' : 'outline'}
                  className="cursor-pointer capitalize"
                  onClick={() => toggleGate(gate)}
                >
                  {formData.assigned_gates.includes(gate) && <Check className="h-3 w-3 mr-1" />}
                  {gate.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {officer ? 'Update' : 'Create'} Officer
          </Button>
        </DialogFooter>
      </form>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-officers-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            Security Officers
          </h3>
          <p className="text-sm text-slate-500">Manage security team members and their roles</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-officer-btn">
          <UserPlus className="h-4 w-4 mr-2" />
          Add Officer
        </Button>
      </div>

      {/* Officers Grid */}
      {officers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No security officers yet</p>
            <p className="text-sm text-slate-500 mt-1">Add your first officer to get started</p>
            <Button onClick={() => setShowAddDialog(true)} className="mt-4">
              <UserPlus className="h-4 w-4 mr-2" />
              Add Officer
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {officers.map((officer) => {
            const role = roles.find(r => r.id === officer.role_id);
            return (
              <Card key={officer.id} className={!officer.is_active ? 'opacity-60' : ''} data-testid={`officer-card-${officer.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${officer.is_active ? 'bg-blue-100' : 'bg-slate-100'}`}>
                        <Shield className={`h-5 w-5 ${officer.is_active ? 'text-blue-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <CardTitle className="text-base">{officer.name}</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          {role && (
                            <Badge 
                              variant="secondary" 
                              className="text-xs"
                              style={{ backgroundColor: `${role.color}20`, color: role.color }}
                            >
                              {role.name}
                            </Badge>
                          )}
                          {!officer.is_active && (
                            <Badge variant="outline" className="text-xs">Inactive</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Switch
                        checked={officer.is_active}
                        onCheckedChange={() => toggleActive(officer)}
                        className="scale-75"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail className="h-4 w-4 text-slate-400" />
                    <span className="truncate">{officer.email}</span>
                  </div>
                  {officer.phone && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Phone className="h-4 w-4 text-slate-400" />
                      <span>{officer.phone}</span>
                    </div>
                  )}
                  {officer.badge_number && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <BadgeCheck className="h-4 w-4 text-slate-400" />
                      <span>{officer.badge_number}</span>
                    </div>
                  )}
                  {officer.assigned_gates?.length > 0 && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      <span className="truncate">{officer.assigned_gates.length} gates</span>
                    </div>
                  )}
                  <div className="flex gap-2 pt-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setEditingOfficer(officer)}
                    >
                      <Edit2 className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-red-600 hover:text-red-700"
                      onClick={() => {
                        if (confirm('Delete this officer?')) {
                          deleteMutation.mutate(officer.id);
                        }
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Security Officer</DialogTitle>
          </DialogHeader>
          <OfficerForm
            officer={null}
            onSubmit={(data) => createMutation.mutate(data)}
            onCancel={() => setShowAddDialog(false)}
            isLoading={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingOfficer} onOpenChange={() => setEditingOfficer(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Security Officer</DialogTitle>
          </DialogHeader>
          {editingOfficer && (
            <OfficerForm
              officer={editingOfficer}
              onSubmit={(data) => updateMutation.mutate({ id: editingOfficer.id, data })}
              onCancel={() => setEditingOfficer(null)}
              isLoading={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecurityOfficersManagement;
